import { Injectable, inject } from '@angular/core';
import {
  Observable,
  defer,
  map,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { CollectionRevisionConnectionTargetDto } from '../interfaces/collection-revision-connection.interface';
import {
  CollectionWorkflowDto,
  CollectionWorkflowTargetDto,
  VersionedCollectionRevisionTargetFileSet,
} from '../interfaces/collection-revision.interface';
import { CollectionRevisionConnectionService } from '../services/collection-revision-connection.service';
import { CollectionRevisionService } from '../services/collection-revision.service';
import { CollectionRevisionTargetRouteIdentity } from './collection-revision-route';

export type LoadedTargetRuntime =
  | {
      readonly mean: 'UPLOAD';
      readonly fileSet: VersionedCollectionRevisionTargetFileSet;
    }
  | {
      readonly mean: 'CONNECTION';
      readonly connectionTarget: CollectionRevisionConnectionTargetDto;
    };

export interface LoadedTargetBundle {
  readonly identity: CollectionRevisionTargetRouteIdentity;
  readonly workflow: CollectionWorkflowDto;
  readonly target: CollectionWorkflowTargetDto;
  readonly runtime: LoadedTargetRuntime;
}

export interface FreshWorkflowHandoff {
  readonly destination: CollectionRevisionTargetRouteIdentity;
  readonly workflow: CollectionWorkflowDto;
}

export class CollectionTargetRouteContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CollectionTargetRouteContractError';
  }
}

export class CollectionTargetRouteConflictError extends Error {
  constructor(
    readonly identity: CollectionRevisionTargetRouteIdentity,
    message = 'The target changed while its exact route was loading.',
  ) {
    super(message);
    this.name = 'CollectionTargetRouteConflictError';
  }
}

class TargetSnapshotMismatch extends Error {}

@Injectable({ providedIn: 'root' })
export class CollectionTargetRouteLoader {
  private readonly revisionService = inject(CollectionRevisionService);
  private readonly connectionService = inject(
    CollectionRevisionConnectionService,
  );
  private readonly consumedHandoffs = new WeakSet<FreshWorkflowHandoff>();

  load(
    identity: CollectionRevisionTargetRouteIdentity,
    handoff?: FreshWorkflowHandoff,
  ): Observable<LoadedTargetBundle> {
    const exactIdentity = freezeIdentity(identity);
    return defer(() => this.resolveExactWorkflow(exactIdentity, handoff)).pipe(
      switchMap((workflow) => {
        const target = requireExactWorkflowTarget(workflow, exactIdentity);
        return this.loadRuntime(exactIdentity, target).pipe(
          map((runtime) => ({
            identity: exactIdentity,
            workflow,
            target,
            runtime,
          })),
          switchMap((bundle) =>
            this.validateWithOneConsistencyRefetch(bundle),
          ),
        );
      }),
    );
  }

  private resolveExactWorkflow(
    identity: CollectionRevisionTargetRouteIdentity,
    handoff?: FreshWorkflowHandoff,
  ): Observable<CollectionWorkflowDto> {
    if (handoff && !this.consumedHandoffs.has(handoff)) {
      this.consumedHandoffs.add(handoff);
      if (
        sameIdentity(handoff.destination, identity) &&
        handoff.workflow.collection_id === identity.collectionId &&
        handoff.workflow.revision_id === identity.revisionId &&
        handoff.workflow.targets.some(
          (target) => target.target_id === identity.targetId,
        )
      ) {
        return of(handoff.workflow);
      }
    }
    return this.fetchExactWorkflow(identity);
  }

  private fetchExactWorkflow(
    identity: CollectionRevisionTargetRouteIdentity,
  ): Observable<CollectionWorkflowDto> {
    return this.revisionService.getExactWorkflow(
      identity.collectionId,
      identity.revisionId,
    );
  }

  private loadRuntime(
    identity: CollectionRevisionTargetRouteIdentity,
    target: CollectionWorkflowTargetDto,
  ): Observable<LoadedTargetRuntime> {
    if (target.mean_type_snapshot === 'UPLOAD') {
      return this.revisionService
        .getTargetFileSet(
          identity.collectionId,
          identity.revisionId,
          identity.targetId,
        )
        .pipe(map((fileSet) => ({ mean: 'UPLOAD', fileSet }) as const));
    }
    if (target.mean_type_snapshot === 'CONNECTION') {
      return this.connectionService
        .getConnectionTarget(identity)
        .pipe(
          map(
            (connectionTarget) =>
              ({ mean: 'CONNECTION', connectionTarget }) as const,
          ),
        );
    }
    return throwError(
      () =>
        new CollectionTargetRouteContractError(
          'The exact workflow target has an unsupported mean.',
        ),
    );
  }

  private validateWithOneConsistencyRefetch(
    bundle: LoadedTargetBundle,
  ): Observable<LoadedTargetBundle> {
    try {
      return of(requireConsistentTargetBundle(bundle));
    } catch (error) {
      if (!(error instanceof TargetSnapshotMismatch)) {
        return throwError(() => error);
      }
    }

    return this.fetchExactWorkflow(bundle.identity).pipe(
      map((workflow) => {
        const target = requireExactWorkflowTarget(workflow, bundle.identity);
        const retried = { ...bundle, workflow, target };
        try {
          return requireConsistentTargetBundle(retried);
        } catch (error) {
          if (error instanceof CollectionTargetRouteContractError) throw error;
          throw new CollectionTargetRouteConflictError(bundle.identity);
        }
      }),
    );
  }
}

export function requireExactWorkflowTarget(
  workflow: CollectionWorkflowDto,
  identity: CollectionRevisionTargetRouteIdentity,
): CollectionWorkflowTargetDto {
  if (
    workflow.collection_id !== identity.collectionId ||
    workflow.revision_id !== identity.revisionId
  ) {
    throw new CollectionTargetRouteContractError(
      'The exact workflow identity does not match the target route.',
    );
  }
  const matches = workflow.targets.filter(
    (target) => target.target_id === identity.targetId,
  );
  if (matches.length !== 1) {
    throw new CollectionTargetRouteContractError(
      'The exact workflow does not contain one matching route target.',
    );
  }
  return matches[0];
}

export function requireConsistentTargetBundle(
  bundle: LoadedTargetBundle,
): LoadedTargetBundle {
  const { identity, workflow, target, runtime } = bundle;
  const exactTarget = requireExactWorkflowTarget(workflow, identity);
  if (
    exactTarget !== target &&
    (exactTarget.target_id !== target.target_id ||
      exactTarget.mean_type_snapshot !== target.mean_type_snapshot)
  ) {
    throw new CollectionTargetRouteContractError(
      'The loaded workflow target does not match its bundle.',
    );
  }

  if (runtime.mean === 'UPLOAD') {
    if (target.mean_type_snapshot !== 'UPLOAD') {
      throw new CollectionTargetRouteContractError(
        'An upload runtime cannot hydrate a connection target.',
      );
    }
    const fileSet = runtime.fileSet.targetFileSet;
    if (
      fileSet.collection_id !== identity.collectionId ||
      fileSet.revision_id !== identity.revisionId ||
      fileSet.target_id !== identity.targetId
    ) {
      throw new CollectionTargetRouteContractError(
        'The exact target file set identity does not match the route.',
      );
    }
    if (
      fileSet.revision_status !== workflow.revision_status ||
      fileSet.target_status !== target.target_status ||
      runtime.fileSet.revisionEtag !== workflow.etag
    ) {
      throw new TargetSnapshotMismatch();
    }
  } else {
    if (target.mean_type_snapshot !== 'CONNECTION') {
      throw new CollectionTargetRouteContractError(
        'A connection runtime cannot hydrate an upload target.',
      );
    }
    const connectionTarget = runtime.connectionTarget;
    if (
      connectionTarget.collection_id !== identity.collectionId ||
      connectionTarget.revision_id !== identity.revisionId ||
      connectionTarget.target_id !== identity.targetId
    ) {
      throw new CollectionTargetRouteContractError(
        'The exact connection target identity does not match the route.',
      );
    }
    if (
      connectionTarget.revision_status !== workflow.revision_status ||
      connectionTarget.target_status !== target.target_status
    ) {
      throw new TargetSnapshotMismatch();
    }
  }

  return deepFreeze({
    identity: { ...identity },
    workflow: {
      ...workflow,
      allowed_actions: [...workflow.allowed_actions],
      targets: workflow.targets.map((candidate) => ({ ...candidate })),
    },
    target: { ...target },
    runtime,
  });
}

function freezeIdentity(
  identity: CollectionRevisionTargetRouteIdentity,
): CollectionRevisionTargetRouteIdentity {
  return Object.freeze({
    collectionId: requireId(identity.collectionId, 'collectionId'),
    revisionId: requireId(identity.revisionId, 'revisionId'),
    targetId: requireId(identity.targetId, 'targetId'),
  });
}

function requireId(value: string, name: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
  return value.trim();
}

function sameIdentity(
  left: CollectionRevisionTargetRouteIdentity,
  right: CollectionRevisionTargetRouteIdentity,
): boolean {
  return (
    left.collectionId === right.collectionId &&
    left.revisionId === right.revisionId &&
    left.targetId === right.targetId
  );
}

function deepFreeze<T>(value: T): T {
  if (typeof value !== 'object' || value === null || Object.isFrozen(value)) {
    return value;
  }
  Object.values(value).forEach((member) => deepFreeze(member));
  return Object.freeze(value);
}
